<template>
  <div class="space-y-4">
    <div
      class="base-shadow bg-background-neutral-secondary w-full rounded-2xl px-4 py-4"
    >
      <h2 class="text-text-neutral-primary mb-4 text-lg font-semibold">
        {{ $t('search.directFlights.title') }}
      </h2>

      <div class="space-y-4">
        <DirectFlightsGroup
          v-for="(group, idx) in visibleGroups"
          :key="`${group.airline.iata}-${idx}`"
          :group="group"
          @onGroupClick="handleGroupClick"
        />
      </div>

      <ExpandableTransition
        v-if="directFlightsStore.groups.length > initialGroupsToShow"
        class="flex flex-col"
      >
        <template #action="{ isExpanded, open, close }">
          <FreeButton
            type="button"
            size="sm"
            class="gray-button order-2 mt-4 flex w-full"
            @click="handleExpandClick(isExpanded, open, close)"
          >
            <div class="text-text-neutral-primary flex items-center gap-2">
              <span class="text-base font-semibold">{{
                isExpanded.value
                  ? $t('general.hide')
                  : $t('search.directFlights.showAll')
              }}</span>
              <ChevronIcon
                class="h-5 w-5 transition-transform"
                :class="isExpanded.value ? 'rotate-180' : 'rotate-0'"
              />
            </div>
          </FreeButton>
        </template>
        <div class="mt-4 space-y-4">
          <DirectFlightsGroup
            v-for="(group, idx) in hiddenGroups"
            :key="`${group.airline.iata}-${idx}`"
            :group="group"
            @onGroupClick="handleGroupClick"
          />
        </div>
      </ExpandableTransition>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted } from 'vue';
  import { ExpandableTransition } from 'shared';
  import { FreeButton, type flightEntity } from 'freedom-shared';
  import type { Dict } from '@aviata/platform-types';

  import { analyticsEventsLib } from '@/shared/lib';

  import ChevronIcon from '@/assets/images/chevron-arrow-20x20.svg?component';

  import DirectFlightsGroup from './DirectFlightsGroup.vue';
  import { useDirectFlightsStore } from '../model';
  import type { DirectFlightGroup } from '../types';

  defineProps<{
    offers: flightEntity.types.MappedOffer[];
    dict: Dict.Entity;
  }>();

  const emit = defineEmits<{
    onGroupClick: [DirectFlightGroup];
  }>();

  const directFlightsStore = useDirectFlightsStore();

  // Show 3 groups for OW, 2 for RT
  const initialGroupsToShow = computed(() =>
    directFlightsStore.isRoundTrip ? 2 : 3,
  );

  const visibleGroups = computed(() =>
    directFlightsStore.groups.slice(0, initialGroupsToShow.value),
  );

  const hiddenGroups = computed(() =>
    directFlightsStore.groups.slice(initialGroupsToShow.value),
  );

  const handleGroupClick = (group: DirectFlightGroup) => {
    emit('onGroupClick', group);
  };

  const handleExpandClick = (
    isExpanded: { value: boolean },
    open: () => void,
    close: () => void,
  ) => {
    if (!isExpanded.value) {
      analyticsEventsLib.directFlightsShowMore();
      open();
    } else {
      close();
    }
  };

  onMounted(() => {
    analyticsEventsLib.directFlightsView();
  });
</script>
